/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import Tooltip from '@mui/material/Tooltip';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { capitalize } from 'lodash';
import { acronymResolver } from "../../utils/acronymResolver";
import { SOURCE_OF_TRUTH_LABELS } from "../render_job_list";
import {
  AssociatedRestaurantCardBody,
  AssociatedRestaurantCardHeader,
  AssociatedRestaurantDiscountChip,
  AssociatedRestaurantDiscountList,
  AssociatedRestaurantMetaIcon,
  AssociatedRestaurantMetaRow,
  AssociatedRestaurantMetaText,
  AssociatedRestaurantName,
  AssociatedRestaurantOverflowTrigger,
  AssociatedRestaurantRoleItem,
  AssociatedRestaurantRoleRow,
  AssociatedRestaurantRoleStatusDot,
  AssociatedRestaurantStatusChip,
  AssociatedRestaurantTypeChip,
  AssociatedRestaurantTypeList,
  AssociatedRestaurantTooltipContent,
} from "./styles";
import {
  formatDisplayLabel,
  formatJoiningDate,
  getReadableTextColor,
  getTypeColor,
} from './helpers';

const getRoleStatusLabel = (isArchived) => (isArchived ? 'Archived role' : 'Active role');

const SourceOfTruthBadge = ({ sourceOfTruth, dataTestId }) => {
  const acronym = acronymResolver(sourceOfTruth);

  if (!acronym) {
    return null;
  }

  return (
    <sup aria-label={`source: ${SOURCE_OF_TRUTH_LABELS[sourceOfTruth] || sourceOfTruth}`} data-testid={dataTestId}>
      {acronym}
    </sup>
  );
};

export const AssociatedRestaurantItemCard = ({ association }) => {
  const jobRoles = Array.isArray(association?.job_role)
    ? association.job_role
      .map((role, index) => {
        const title = formatDisplayLabel(role);

        if (title === '-') {
          return null;
        }

        return {
          id: role?.id || `${association?.restaurant_id || 'restaurant'}-role-${index}`,
          title,
          archived: Boolean(role?.archived ?? role?.archive),
          sourceOfTruth: role?.source_of_truth,
        };
      })
      .filter(Boolean)
    : [];
  const primaryRole = jobRoles[0] || null;
  const extraRoles = jobRoles.slice(1);
  const discountCards = Array.isArray(association?.discount_cards)
    ? association.discount_cards
    : [];
  const associationTypes = Array.isArray(association?.type)
    ? association.type.map(formatDisplayLabel).filter(Boolean)
    : [];

  return (
    <AssociatedRestaurantCardBody data-testid={`associated-restaurant-card-${association?.restaurant_id}`}>
      <AssociatedRestaurantCardHeader>
        <AssociatedRestaurantName data-testid="associated-restaurant-name">
          {association?.restaurant_name || '-'}
        </AssociatedRestaurantName>
        <AssociatedRestaurantStatusChip
          label={association?.active ? 'ACTIVE' : 'INACTIVE'}
          isActive={Boolean(association?.active)}
          size="small"
          data-testid="associated-restaurant-status"
        />
      </AssociatedRestaurantCardHeader>

      {!!associationTypes.length && (
        <AssociatedRestaurantTypeList>
          {associationTypes.map((typeValue) => {
            const colorConfig = getTypeColor(typeValue);

            return (
              <AssociatedRestaurantTypeChip
                key={typeValue}
                label={capitalize(typeValue)}
                size="small"
                backgroundColor={colorConfig.background}
                textColor={colorConfig.color}
                data-testid="associated-restaurant-type-chip"
              />
            );
          })}
        </AssociatedRestaurantTypeList>
      )}

      <AssociatedRestaurantMetaRow>
        <AssociatedRestaurantMetaIcon>
          <BusinessCenterOutlinedIcon sx={{ fontSize: '13px' }} />
        </AssociatedRestaurantMetaIcon>
        <AssociatedRestaurantRoleRow>
          <AssociatedRestaurantRoleItem>
            {primaryRole && (
              <AssociatedRestaurantRoleStatusDot
                isArchived={primaryRole.archived}
                role="img"
                aria-label={getRoleStatusLabel(primaryRole.archived)}
                title={getRoleStatusLabel(primaryRole.archived)}
                data-testid="associated-restaurant-primary-role-dot"
              />
            )}
            <AssociatedRestaurantMetaText data-testid="associated-restaurant-primary-role">
              {primaryRole?.title || '-'}{' '}
              {primaryRole && (
                <SourceOfTruthBadge
                  sourceOfTruth={primaryRole.sourceOfTruth}
                  dataTestId="associated-restaurant-primary-role-source"
                />
              )}
            </AssociatedRestaurantMetaText>
          </AssociatedRestaurantRoleItem>
          {!!extraRoles.length && (
            <Tooltip
              arrow
              placement="top"
              title={
                <AssociatedRestaurantTooltipContent>
                  {extraRoles.map((role, index) => (
                    <AssociatedRestaurantRoleItem
                      key={role.id}
                      data-testid={`associated-restaurant-tooltip-role-${index}`}
                    >
                      <AssociatedRestaurantRoleStatusDot
                        isArchived={role.archived}
                        role="img"
                        aria-label={getRoleStatusLabel(role.archived)}
                        title={getRoleStatusLabel(role.archived)}
                        data-testid={`associated-restaurant-tooltip-role-dot-${index}`}
                      />
                      <span>
                        {role.title}{' '}
                        <SourceOfTruthBadge
                          sourceOfTruth={role.sourceOfTruth}
                          dataTestId={`associated-restaurant-tooltip-role-source-${index}`}
                        />
                      </span>
                    </AssociatedRestaurantRoleItem>
                  ))}
                </AssociatedRestaurantTooltipContent>
              }
            >
              <AssociatedRestaurantOverflowTrigger data-testid="associated-restaurant-more-roles">
                +{extraRoles.length}
              </AssociatedRestaurantOverflowTrigger>
            </Tooltip>
          )}
        </AssociatedRestaurantRoleRow>
      </AssociatedRestaurantMetaRow>

      <AssociatedRestaurantMetaRow>
        <AssociatedRestaurantMetaIcon>
          <CalendarMonthOutlinedIcon sx={{ fontSize: '13px' }} />
        </AssociatedRestaurantMetaIcon>
        <AssociatedRestaurantMetaText data-testid="associated-restaurant-joining-date">
          Since {formatJoiningDate(association?.joining_date)}
        </AssociatedRestaurantMetaText>
      </AssociatedRestaurantMetaRow>

      {!!discountCards.length && (
        <AssociatedRestaurantDiscountList>
          {discountCards.map((discountCard, index) => (
            <AssociatedRestaurantDiscountChip
              key={discountCard?.id || `${discountCard?.name}-${index}`}
              label={discountCard?.name || '-'}
              size="small"
              chipbackground={discountCard?.color || '#556476'}
              chiptextcolor={getReadableTextColor(discountCard?.color || '#556476')}
              data-testid="associated-restaurant-discount-chip"
            />
          ))}
        </AssociatedRestaurantDiscountList>
      )}
    </AssociatedRestaurantCardBody>
  );
};
